import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import PageLayout from "../../components/shared/PageLayout";
import BoxResponsesFilterTabs, {
  type FilterTab,
} from "./BoxResponsesFilterTabs";
import AnswerDetailSheet from "./components/AnswerDetailSheet";
import BoxShortCodeLine from "./components/BoxShortCodeLine";
import BoxTitleGradientCard from "./components/BoxTitleGradientCard";
import ResponsesList from "./components/ResponsesList";
import StateMessage from "./components/StateMessage";
import { StyledBoxResponses } from "./index.styled";
import type { BoxAnswer, CreatedBox } from "../../types/dashboard";
import {
  fetchBoxByShortCode,
  fetchQuestionsByBoxId,
  updateQuestionStatus,
} from "../../utils/apiService";

const isDeleted = (a: BoxAnswer) => a.status === "deleted";

const BoxResponses = () => {
  const { id: shortCode } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [box, setBox] = useState<CreatedBox | null>(null);
  const [answers, setAnswers] = useState<BoxAnswer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterTab>("pending");
  const [selected, setSelected] = useState<BoxAnswer | null>(null);
  const [statusUpdatingId, setStatusUpdatingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!shortCode) return;
    setLoading(true);
    setError(null);
    try {
      const boxData = await fetchBoxByShortCode(shortCode);
      setBox(boxData);
      const list = await fetchQuestionsByBoxId(boxData.id);
      setAnswers(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error(e);
      setError("לא ניתן לטעון את התגובות");
      setBox(null);
      setAnswers([]);
    } finally {
      setLoading(false);
    }
  }, [shortCode]);

  useEffect(() => {
    void load();
  }, [load]);

  const visible = useMemo(
    () => answers.filter((a) => !isDeleted(a)),
    [answers],
  );

  const counts = useMemo(() => {
    const pending = visible.filter((a) => a.status === "pending").length;
    const viewed = visible.filter((a) => a.status === "viewed").length;
    const answered = visible.filter((a) => a.status === "answered").length;
    return { pending, viewed, answered };
  }, [visible]);

  const filteredList = useMemo(() => {
    return visible.filter((a) => a.status === filter);
  }, [visible, filter]);

  const displayNumberFor = useCallback(
    (answerId: string) => {
      const idx = visible.findIndex((a) => a.id === answerId);
      return idx >= 0 ? visible.length - idx : 0;
    },
    [visible],
  );

  const openAnswer = async (answer: BoxAnswer) => {
    setSelected(answer);
    if (answer.status !== "pending" || statusUpdatingId) return;
    setStatusUpdatingId(answer.id);
    try {
      const updated = await updateQuestionStatus(answer.id, "viewed");
      setAnswers((prev) =>
        prev.map((a) => (a.id === updated.id ? { ...a, ...updated } : a)),
      );
      setSelected((prev) =>
        prev?.id === updated.id ? { ...prev, ...updated } : prev,
      );
    } catch (e) {
      console.error(e);
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const handleStatusChange = async (
    answerId: string,
    newStatus: "viewed" | "answered",
  ) => {
    if (statusUpdatingId) return;
    setStatusUpdatingId(answerId);
    try {
      const updated = await updateQuestionStatus(answerId, newStatus);
      setAnswers((prev) =>
        prev.map((a) => (a.id === updated.id ? { ...a, ...updated } : a)),
      );
      setSelected((prev) =>
        prev?.id === updated.id ? { ...prev, ...updated } : prev,
      );
    } catch (e) {
      console.error(e);
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const toggleStatus = async (answer: BoxAnswer) => {
    if (statusUpdatingId) return;
    const newStatus = answer.status === "pending" ? "viewed" : "pending";
    setStatusUpdatingId(answer.id);
    try {
      const updated = await updateQuestionStatus(answer.id, newStatus);
      setAnswers((prev) =>
        prev.map((a) => (a.id === updated.id ? { ...a, ...updated } : a)),
      );
    } catch (e) {
      console.error(e);
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const closeSheet = () => setSelected(null);

  return (
    <PageLayout
      title="שאלות אנונימיות"
      StyledComponent={StyledBoxResponses}
      containerClassName="box-responses-container"
      onBack={() => navigate(`/anonymous/${shortCode}`)}
    >
      {loading && <StateMessage>טוען תגובות…</StateMessage>}

      {!loading && error && (
        <StateMessage variant="error">{error}</StateMessage>
      )}

      {!loading && !error && box && (
        <>
          <BoxShortCodeLine shortCode={box.short_code} />
          <BoxTitleGradientCard title={box.title} />
          <BoxResponsesFilterTabs
            filter={filter}
            onFilterChange={setFilter}
            counts={counts}
          />

          {filteredList.length === 0 ? (
            <StateMessage>אין תגובות להצגה בסינון זה</StateMessage>
          ) : (
            <ResponsesList
              items={filteredList}
              onSelect={openAnswer}
              onToggleStatus={toggleStatus}
              updatingId={statusUpdatingId}
            />
          )}
        </>
      )}

      {selected && box && (
        <AnswerDetailSheet
          displayNumber={displayNumberFor(selected.id)}
          questionText={selected.question_text}
          boxTitle={box.title}
          boxShortCode={box.short_code}
          onClose={closeSheet}
          onStatusChange={(status) => handleStatusChange(selected.id, status)}
        />
      )}
    </PageLayout>
  );
};

export default BoxResponses;
